import { Box } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Button from 'UI/Button/ButtonWithPreloader'
import ModalComponent from 'UI/Modal'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import { ReactComponent as ArrowLeftIcon } from 'assets/svg/ArrowLeftIcon.svg'
import { ReactComponent as EditPenIcon } from 'assets/svg/EditPenIcon.svg'
import { ReactComponent as UploadIcon } from 'assets/svg/UploadIcon.svg'
import BusinessInfo from 'components/CompanyInfo/BusinessInfo'
import UserPageInfo from 'components/UserPageInfo'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { EDIT_VENDOR_PERM, TRANSACTION_PERM, UPLOAD_USER_PAGE_DOCS_PERM, VENDORS_MODULE_PERM, WITHDRAWAL_PERM } from 'constants/permissions'
import { TRANSACTIONS_URL, VENDOR_DOCUMENTS, VENDOR_EDIT_URL, WITHDRAWAL_URL } from 'constants/routes'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'
import { TableIsoDocuments, useIso } from 'hooks/useIso'
import { usePermission } from 'hooks/usePermission'
import { useSnackbar } from 'notistack'
import React, { FC, useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { getFile } from 'services/storage'
import { deleteFileFromVendor, getVendorById } from 'services/vendor'
import { createLinkToDownLoad } from 'utils/downloadFile'
import { convertEmptyField } from 'utils/formatters'
import { VendorType } from 'typescript/interfaces/vendor'

const InfoVendorContainer: FC = () => {
  const { goBack, push } = useHistory()
  const { hasPermission, isOwnVendor } = usePermission()
  const aStyle = useAdditionalMaterialStyle()
  const queryCache = useQueryCache()
  const { enqueueSnackbar } = useSnackbar()

  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery(['vendorById', id], () => getVendorById(id), {
    retry: false,
    cacheTime: 0,
  })

  const [deleteFileState, setDeleteFileState] = useState({
    id: 0,
    open: false,
  })

  const [removeDoc, { isLoading: isLoadingRemove }] = useMutation(deleteFileFromVendor, {
    onSuccess: (_, variables) => {
      setDeleteFileState({
        open: false,
        id: 0,
      })
      queryCache.setQueryData(['vendorById', id], {
        ...data,
        data: {
          ...data?.data,
          documents: data?.data.documents.filter((d) => d.id !== variables),
        },
      })
      enqueueSnackbar('Successfully deleted document')
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const handleDownLoadFile = useCallback((url, name) => {
    getFile(url).then((res) => {
      createLinkToDownLoad(res.data, name)
    })
  }, [])

  const { columnsIsoDocuments: columnsVendorDocuments } = useIso({
    handleDeleteFile: (idFile) => {
      setDeleteFileState({
        id: idFile,
        open: true,
      })
    },
    handleDownloadFile: handleDownLoadFile,
    canDelete: hasPermission(VENDORS_MODULE_PERM),
  })

  return (
    <Box className={aStyle.wrapper}>
      {isLoading || !data ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center">
            <Box ml="3rem" mr="1.5rem">
              <IconButton color="inherit" onClick={() => goBack()}>
                <ArrowLeftIcon />
              </IconButton>
            </Box>
            <Typography color="textSecondary" variant="h1">
              {data.data.businessName}
            </Typography>
            <Box mr="3rem" flex="1" display="flex" justifyContent="flex-end">
              {(hasPermission(TRANSACTION_PERM) || isOwnVendor(data?.data.id)) && (
                <Button
                  onClick={() =>
                    push(
                      generatePath(TRANSACTIONS_URL, {
                        id: data.data.id,
                        type: 'vendor',
                        businessName: data.data.businessName,
                      }),
                    )
                  }
                  variant="outlined"
                  color="secondary"
                >
                  Activity
                </Button>
              )}
              {(hasPermission(WITHDRAWAL_PERM) || isOwnVendor(data?.data.id)) && (
                <Box ml="1rem">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      push(
                        generatePath(WITHDRAWAL_URL, {
                          id: data.data.id,
                          type: 'vendor',
                          businessName: data.data.businessName,
                        }),
                      )
                    }
                  >
                    Withdrawals
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Box mt="2rem">
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Paper elevation={0}>
                  <Box width="100%" display="flex" justifyContent="flex-end" p="1rem" position="relative">
                    {hasPermission(EDIT_VENDOR_PERM) && (
                      <IconButton
                        className={aStyle.absoluteEditButton}
                        size="small"
                        onClick={() =>
                          push(
                            generatePath(VENDOR_EDIT_URL, {
                              id: data.data.id.toString(),
                            }),
                          )
                        }
                      >
                        <EditPenIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Box pb="2rem" px="3rem">
                    <Box mb="2rem">
                      <Typography variant="h3" color="textSecondary">
                        General Information
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        container
                        xs={6}
                        spacing={3}
                        style={{
                          alignContent: 'flex-start',
                        }}
                      >
                        <Grid item xs={6}>
                          <Typography>Vendor Type</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">
                            {(data.data.vendorType ? VendorType[data.data.vendorType as keyof typeof VendorType] : data.data.vendorType)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>EIN</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.ein)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>E-mail</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.email)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Phone</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.phone)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Comments</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.comments)}</Typography>
                        </Grid>
                      </Grid>
                      <Grid item container xs={6} spacing={3}>
                        <UserPageInfo userPage={data.data.userPage} nameOnCheck={data.data.nameOnCheck} />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={0}>
                  <BusinessInfo
                    street={data.data.street}
                    businessName={data.data.businessName}
                    city={data.data.city}
                    state={data.data.state}
                    federalTaxId={data.data.federalTaxId}
                    zipCode={data.data.zipCode}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box mt={5}>
            <Paper elevation={0}>
              <Box p="3rem" display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h3" color="textSecondary">
                  Documents
                </Typography>
                <Box>
                  {(hasPermission(UPLOAD_USER_PAGE_DOCS_PERM) || isOwnVendor(data?.data.id)) && (
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => push(generatePath(VENDOR_DOCUMENTS, { id: data.data.id }))}
                    >
                      Upload documents
                    </Button>
                  )}
                </Box>
              </Box>
              <Box>
                <TableIsoDocuments columns={columnsVendorDocuments} data={data.data.documents} />
              </Box>
            </Paper>
          </Box>
        </>
      )}
      <ModalComponent open={deleteFileState.open}>
        <ConfirmationContent
          text="Are you sure you want delete file?"
          isLoading={isLoadingRemove}
          handleCancel={() =>
            setDeleteFileState({
              open: false,
              id: 0,
            })
          }
          handleConfirm={() => removeDoc(deleteFileState.id)}
        />
      </ModalComponent>
    </Box>
  )
}
export default InfoVendorContainer
